import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import JitsiRoom from "@/pages/meetings/JitsiRoom";
import GuestConvertCard from "@/pages/public/GuestConvertCard";
import { toast } from "sonner";
import {
  Loader2, Video, Send, ShieldCheck, ListChecks, FileText, Clock, HeartHandshake, Lock,
  DoorOpen, MessageCircle, CheckCircle2, Mail, Upload, Download, ClipboardCheck,
} from "lucide-react";

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export default function GuestRoom() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessCode, setAccessCode] = useState("");
  const [entering, setEntering] = useState(false);
  const [token, setToken] = useState(() => sessionStorage.getItem(`mmm_guest_${code}`) || "");
  const [room, setRoom] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [docs, setDocs] = useState([]);
  const [emailing, setEmailing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [gvideo, setGvideo] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    publicApi.get(`/guest/room/${code}/info`).then(({ data }) => setInfo(data)).catch(() => setInfo({ exists: false })).finally(() => setLoading(false));
  }, [code]);

  const loadRoom = useCallback(async (tk) => {
    try { const { data } = await publicApi.get("/guest/room", auth(tk)); setRoom(data.room); }
    catch { setToken(""); sessionStorage.removeItem(`mmm_guest_${code}`); }
  }, [code]);
  const loadMsgs = useCallback(async (tk) => {
    try { const { data } = await publicApi.get("/guest/room/messages", auth(tk)); setMsgs(data.messages || []); } catch { /* noop */ }
  }, []);
  const loadDocs = useCallback(async (tk) => {
    try { const { data } = await publicApi.get("/guest/room/documents", auth(tk)); setDocs(data.documents || []); } catch { /* noop */ }
  }, []);

  useEffect(() => { if (token) { loadRoom(token); loadMsgs(token); loadDocs(token); } }, [token]); // eslint-disable-line
  useEffect(() => { if (!token) return; const t = setInterval(() => loadMsgs(token), 6000); return () => clearInterval(t); }, [token, loadMsgs]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs]);

  const enter = async (e) => {
    e?.preventDefault();
    setEntering(true);
    try {
      const { data } = await publicApi.post(`/guest/room/${code}/enter`, { access_code: accessCode.trim() });
      sessionStorage.setItem(`mmm_guest_${code}`, data.access_token);
      setToken(data.access_token); setRoom(data.room);
    } catch (err) { toast.error(err?.response?.data?.detail || "That code didn't work."); }
    finally { setEntering(false); }
  };
  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try { await publicApi.post("/guest/room/messages", { text: reply.trim() }, auth(token)); setReply(""); loadMsgs(token); }
    catch { toast.error("Message failed."); } finally { setSending(false); }
  };
  const emailLink = async () => {
    setEmailing(true);
    try {
      const { data } = await publicApi.post(`/guest/room/${code}/email-link`, { base_url: window.location.origin });
      toast.success(`Sent to ${data.masked_email}. Please don't forward it.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send the email."); }
    finally { setEmailing(false); }
  };
  const downloadDoc = async (d) => {
    try { const res = await publicApi.get(`/guest/room/documents/${d.id}/download`, { ...auth(token), responseType: "blob" }); const url = URL.createObjectURL(res.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("Couldn't open the file."); }
  };
  const uploadDoc = async (file, docId) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("name", file.name); fd.append("doc_type", "other");
      if (docId) fd.append("doc_id", docId);
      await publicApi.post("/guest/room/documents/upload", fd, auth(token));
      toast.success("Thank you — your file was uploaded."); loadDocs(token);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };
  const markReview = async () => {
    try { await publicApi.post("/guest/room/review-complete", {}, auth(token)); toast.success("Review marked complete."); loadMsgs(token); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed."); }
  };
  const joinVideo = async () => {
    try { const { data } = await publicApi.get("/guest/room/video", auth(token)); setGvideo(data.video); }
    catch { toast.error("Couldn't start the video."); }
  };

  if (loading) return <div className="min-h-screen flex justify-center items-center"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;

  // Invalid / expired
  if (!info?.exists || info?.expired) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F1F5F8] px-4" data-testid="guest-room-invalid">
      <div className="max-w-md text-center bg-white rounded-2xl border border-[#E6EDF2] p-8">
        <Clock className="mx-auto mb-3 text-[#9AA5B1]" size={30} />
        <h1 className="text-xl font-display font-bold text-[#1C4358]">This room link isn't available</h1>
        <p className="mt-2 text-[15px] text-[#4B5563]">{info?.expired ? "This secure link has expired." : "We couldn't find that room."} Please contact your MovingMyMama guide for a fresh link.</p>
      </div>
    </div>
  );

  // Join screen (code entry)
  if (!token || !room) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F1F5F8] px-4" data-testid="guest-room-join">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[#E6EDF2] p-8">
        <div className="text-center">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1C4358]/8 text-[#1C4358]"><DoorOpen size={24} /></span>
          <h1 className="mt-3 text-2xl font-display font-bold text-[#1C4358]">Join your secure room</h1>
          <p className="mt-2 text-[15px] text-[#4B5563]">You do not have to figure this out alone. This room gives your family one clear place to talk, share, and move forward.</p>
          {info.meeting_title && <p className="mt-3 text-[14px] font-medium text-[#1C4358]">{info.meeting_title}</p>}
        </div>
        <form onSubmit={enter} className="mt-6">
          <label className="text-[13px] font-semibold text-[#1C4358]">Enter your access code</label>
          <input data-testid="guest-code-input" value={accessCode} onChange={(e) => setAccessCode(e.target.value)} inputMode="numeric" placeholder="6-digit code"
            className="mt-1 w-full h-14 text-center text-[24px] tracking-[0.4em] font-mono rounded-xl border border-[#D6E0E7] outline-none focus:border-[#1C4358]" />
          <button type="submit" data-testid="guest-enter-btn" disabled={entering}
            className="mmm-tap mt-4 w-full h-13 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold text-[16px] inline-flex items-center justify-center gap-2">
            {entering ? <Loader2 size={18} className="animate-spin" /> : <DoorOpen size={18} />} Enter room
          </button>
        </form>
        <button type="button" data-testid="guest-email-link-btn" onClick={emailLink} disabled={emailing}
          className="mmm-tap mt-3 w-full h-11 rounded-full border border-[#1C4358]/30 text-[#1C4358] font-medium text-[14px] inline-flex items-center justify-center gap-2 hover:bg-[#1C4358]/6">
          {emailing ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Email me this link &amp; code
        </button>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-[12px] text-[#4B5563]"><ShieldCheck size={13} className="text-[#2F7D5A]" /> This room is private and only for you.</p>
      </div>
    </div>
  );

  // Room
  const steps = [
    { icon: DoorOpen, label: "Join the secure room", done: true },
    { icon: MessageCircle, label: "Meet by video or chat by text", done: false },
    { icon: FileText, label: "Review documents & next steps", done: false },
  ];

  return (
    <div className="min-h-screen bg-[#F1F5F8]" data-testid="guest-room">
      <div className="bg-[#1C4358] text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><HeartHandshake size={20} /><span className="font-display font-bold">MovingMyMama</span></div>
          <span className="text-[12px] text-white/70">Welcome{room.guest?.name ? `, ${room.guest.name}` : ""}</span>
        </div>
      </div>

      {/* 1-2-3 steps */}
      <div className="max-w-5xl mx-auto px-4 pt-5">
        <div className="grid grid-cols-3 gap-2">
          {steps.map((s, i) => (
            <div key={i} className={`rounded-xl border p-3 text-center ${s.done ? "border-[#2F7D5A]/40 bg-[#2F7D5A]/6" : "border-[#E6EDF2] bg-white"}`}>
              <s.icon size={18} className={`mx-auto ${s.done ? "text-[#2F7D5A]" : "text-[#1C4358]"}`} />
              <div className="text-[11px] sm:text-[12px] font-semibold text-[#1C4358] mt-1">Step {i + 1}</div>
              <div className="text-[11px] sm:text-[12px] text-[#4B5563]">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {gvideo ? (
            <div data-testid="guest-video-live">
              <JitsiRoom video={gvideo} onLeave={() => setGvideo(null)} height={420} />
              <button onClick={() => setGvideo(null)} className="mt-1 text-[13px] text-[#9C1E22] hover:underline">Leave video</button>
            </div>
          ) : (
            <div className="rounded-2xl bg-[#0f2733] text-white p-8 text-center" data-testid="guest-video-shell">
              <Video size={30} className="mx-auto mb-2 opacity-80" />
              <div className="font-display font-semibold text-[17px]">{room.title}</div>
              <div className="text-[13px] text-white/70 mt-1">Join the video when your guide is ready. Recording is off.</div>
              <button onClick={joinVideo} data-testid="guest-join-video-btn" className="mmm-tap mt-3 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold"><Video size={18} /> Join video</button>
              <div className="text-[12px] text-white/50 mt-2">Host: {room.host_name}</div>
            </div>
          )}

          {(room.checklist || []).length > 0 && (
            <div className="rounded-2xl bg-white border border-[#E6EDF2] p-5" data-testid="guest-checklist">
              <div className="flex items-center gap-1.5 font-display font-semibold text-[#1C4358] mb-2"><ListChecks size={16} /> Your next steps</div>
              <div className="space-y-1.5">
                {room.checklist.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-[15px]">
                    <CheckCircle2 size={16} className={c.done ? "text-[#2F7D5A]" : "text-[#D6E0E7]"} />
                    <span className={c.done ? "line-through text-[#9AA5B1]" : "text-[#252221]"}>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents (Phase 10B) */}
          <div className="rounded-2xl bg-white border border-[#E6EDF2] p-5" data-testid="guest-documents">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 font-display font-semibold text-[#1C4358]"><FileText size={16} /> Documents</div>
              <label className="mmm-tap inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#1C4358] hover:bg-[#163545] text-white text-[13px] font-medium cursor-pointer" data-testid="guest-doc-upload-btn">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
                <input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadDoc(e.target.files[0])} />
              </label>
            </div>
            {docs.length === 0 ? <p className="text-[14px] text-[#9AA5B1]">Nothing here yet. Upload a file if your guide asked for one.</p> : (
              <div className="space-y-2">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 rounded-xl border border-[#E6EDF2] p-2.5" data-testid={`guest-doc-${d.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-medium text-[#1C4358] truncate">{d.name}</div>
                      <div className="text-[12px] text-[#4B5563]">{d.uploaded ? (d.status_label || "Uploaded") : "Requested — please upload"}</div>
                    </div>
                    {d.uploaded ? (
                      <button onClick={() => downloadDoc(d)} className="mmm-tap h-9 w-9 rounded-full bg-[#F1F5F8] text-[#1C4358] flex items-center justify-center" data-testid={`guest-doc-download-${d.id}`}><Download size={16} /></button>
                    ) : (
                      <label className="mmm-tap h-9 px-3 rounded-full bg-[#2F7D5A] text-white text-[13px] font-medium inline-flex items-center gap-1.5 cursor-pointer" data-testid={`guest-doc-fulfill-${d.id}`}>
                        <Upload size={14} /> Upload<input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadDoc(e.target.files[0], d.id)} />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="mt-2 text-[12px] text-[#9AA5B1]">Only your guide and people invited to this room can see these files.</p>
          </div>

          {room.guest?.role === "professional_guest" && (
            <button onClick={markReview} data-testid="guest-review-complete-btn"
              className="mmm-tap w-full h-12 rounded-2xl bg-[#2F7D5A] hover:bg-[#276848] text-white font-semibold text-[15px] inline-flex items-center justify-center gap-2">
              <ClipboardCheck size={18} /> Mark my review complete
            </button>
          )}

          {room.guest?.role === "temporary_guest" && (
            <GuestConvertCard token={token} guest={room.guest} />
          )}
        </div>

        {/* Text chat */}
        <div className="rounded-2xl bg-white border border-[#E6EDF2] flex flex-col" style={{ height: 460 }} data-testid="guest-chat">
          <div className="px-4 py-3 border-b border-[#E6EDF2] font-display font-semibold text-[#1C4358] flex items-center gap-1.5"><MessageCircle size={16} /> Chat with your guide</div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
            {msgs.length === 0 && <div className="text-[13px] text-[#9AA5B1] text-center py-8">Say hello — your guide is here to help.</div>}
            {msgs.map((mm) => (
              <div key={mm.id} className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[14px] ${mm.author_kind === "guest" ? "ml-auto bg-[#9C1E22] text-white" : "bg-[#F1F5F8] text-[#252221]"}`}>
                <div className="text-[10px] opacity-70 mb-0.5">{mm.author_kind === "guest" ? "You" : mm.author_name}</div>
                {mm.text}
              </div>
            ))}
          </div>
          <div className="border-t border-[#E6EDF2] p-2 flex gap-2">
            <input data-testid="guest-chat-input" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 h-11 rounded-full border border-[#D6E0E7] px-4 text-[15px] outline-none focus:border-[#1C4358]" />
            <button data-testid="guest-chat-send" onClick={send} disabled={sending} className="mmm-tap h-11 w-11 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white flex items-center justify-center">{sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
          </div>
        </div>
      </div>

      <p className="max-w-5xl mx-auto px-4 pb-8 flex items-center justify-center gap-1.5 text-[12px] text-[#4B5563]"><Lock size={12} /> This is your private room. General guidance only — a qualified professional should review your family's specific situation.</p>
    </div>
  );
}
